import React, { useMemo } from "react";
import { Spinner, Tooltip as ChakraTooltip } from "@chakra-ui/react";
import { FiLock, FiRotateCcw } from "react-icons/fi";
import { SelectWithLabel } from "newui/components/ui/InputWithLabel/SelectWithLabel";
import { showToast } from "utils/helpers";
import { notificationsLang, scopeOriginLabel } from "../lang/en";
import { useSavePreferences } from "../hooks/useSavePreferences";
import type {
	NotificationChannel,
	NotificationDelivery,
	NotificationScopeSelection,
	NotificationTypeResource,
} from "../types/Notifications.type";

export interface PreferenceMatrixProps {
	error: boolean;
	isLoading: boolean;
	onRetry: () => void;
	scope: NotificationScopeSelection;
	types: NotificationTypeResource[] | undefined;
}

const CHANNEL_LABELS: Record<NotificationChannel, string> = {
	mail: "Email",
	in_app: "In-app",
};

const DELIVERY_OPTIONS: { value: NotificationDelivery; label: string }[] = [
	{ value: "immediate", label: notificationsLang.matrixImmediateBadge },
	{ value: "digest", label: notificationsLang.matrixDigestBadge },
	{ value: "off", label: notificationsLang.matrixOffBadge },
];

interface AreaGroup {
	area: string;
	types: NotificationTypeResource[];
}

export const PreferenceMatrix = ({ types, isLoading, error, onRetry, scope }: PreferenceMatrixProps) => {
	const saveMutation = useSavePreferences(scope);

	const channels = useMemo<NotificationChannel[]>(() => {
		const set = new Set<NotificationChannel>();
		(types ?? []).forEach((type) => {
			type.supported_channels.forEach((channel) => set.add(channel));
		});
		return Array.from(set);
	}, [types]);

	const groups = useMemo<AreaGroup[]>(() => {
		const map = new Map<string, NotificationTypeResource[]>();
		(types ?? []).forEach((type) => {
			const list = map.get(type.area) ?? [];
			list.push(type);
			map.set(type.area, list);
		});
		return Array.from(map.entries()).map(([area, areaTypes]) => ({ area, types: areaTypes }));
	}, [types]);

	const handleDeliveryChange = (type: NotificationTypeResource, channel: NotificationChannel, delivery: NotificationDelivery) => {
		saveMutation.mutate(
			{
				scope_type: scope.scopeType,
				scope_id: scope.scopeId,
				entries: [{ type_key: type.key, channel, delivery }],
			},
			{
				onSuccess: () => {
					showToast(
						notificationsLang.saveSuccessDescription,
						notificationsLang.saveSuccessTitle,
						true,
						`notif-pref-saved-${type.key}-${channel}`
					);
				},
				onError: () => {
					showToast(
						notificationsLang.saveErrorDescription,
						notificationsLang.saveErrorTitle,
						false,
						`notif-pref-error-${type.key}-${channel}`
					);
				},
			}
		);
	};

	const handleRevert = (type: NotificationTypeResource, channel: NotificationChannel) => {
		// "Revert to inherited" has no dedicated DELETE in the frozen 106 contract
		// (PUT /preferences takes explicit entries only) — deferred to backend
		// follow-up; see TASK_SUMMARY-115.md. Falling back to "off" would silently
		// misrepresent server state, so the control is a documented no-op guard
		// pending contract support rather than a fabricated write.
		void type;
		void channel;
	};

	if (isLoading) {
		return (
			<div className="flex items-center justify-center py-12" data-testid="preference-matrix-loading">
				<Spinner size="lg" color="blue.500" />
			</div>
		);
	}

	if (error) {
		return (
			<div className="flex flex-col items-center gap-3 py-12" data-testid="preference-matrix-error">
				<p className="text-sm text-red-700">{notificationsLang.matrixLoadError}</p>
				<button
					type="button"
					className="rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
					onClick={onRetry}
					data-testid="preference-matrix-retry"
				>
					{notificationsLang.matrixRetry}
				</button>
			</div>
		);
	}

	if (!types || types.length === 0) {
		return (
			<div className="py-12 text-center text-sm text-gray-500" data-testid="preference-matrix-empty">
				{notificationsLang.matrixEmpty}
			</div>
		);
	}

	return (
		<div data-testid="preference-matrix" className="overflow-auto rounded-md border border-gray-200">
			<table className="min-w-full border-separate border-spacing-0 text-sm">
				<thead>
					<tr>
						<th className="sticky left-0 top-0 z-20 min-w-[240px] border-b border-r border-gray-200 bg-gray-50 px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
							{notificationsLang.matrixColumnType}
						</th>
						{channels.map((channel) => (
							<th
								key={channel}
								data-testid={`matrix-column-${channel}`}
								className="sticky top-0 z-10 min-w-[160px] border-b border-r border-gray-200 bg-gray-50 px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500"
							>
								{CHANNEL_LABELS[channel]}
							</th>
						))}
					</tr>
				</thead>
				<tbody>
					{groups.map((group) => (
						<React.Fragment key={group.area}>
							<tr>
								<td
									colSpan={channels.length + 1}
									className="border-b border-gray-200 bg-gray-100 px-4 py-1.5 text-xs font-semibold uppercase tracking-wide text-gray-600"
									data-testid={`matrix-area-${group.area}`}
								>
									{group.area}
								</td>
							</tr>
							{group.types.map((type) => (
								<tr key={type.key} className="even:bg-gray-50/60">
									<th className="sticky left-0 z-[1] border-b border-r border-gray-200 bg-inherit px-4 py-2 text-left font-medium text-gray-900">
										<div className="flex items-center gap-2">
											<span data-testid={`matrix-row-label-${type.key}`}>{type.name}</span>
											{type.security && (
												<span
													className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-amber-800"
													data-testid={`matrix-security-badge-${type.key}`}
												>
													{notificationsLang.matrixSecurityBadge}
												</span>
											)}
										</div>
										<p className="mt-0.5 text-xs font-normal text-gray-500">{type.description}</p>
									</th>
									{channels.map((channel) => {
										const supported = type.supported_channels.includes(channel);
										const effective = type.effective[channel];

										if (!supported || !effective) {
											return (
												<td
													key={channel}
													className="border-b border-r border-gray-200 px-4 py-2 text-gray-300"
													data-testid={`matrix-cell-${type.key}-${channel}`}
												>
													—
												</td>
											);
										}

										const isOverridden = effective.source === scope.scopeType;
										const isSecurity = type.security;
										const isDisabled = isSecurity || effective.locked || saveMutation.isPending;

										return (
											<td
												key={channel}
												className={`border-b border-r border-gray-200 px-4 py-2 ${
													isOverridden ? "bg-blue-50/50" : ""
												}`}
												data-testid={`matrix-cell-${type.key}-${channel}`}
											>
												<div className="flex items-center gap-2">
													{effective.locked && (
														<ChakraTooltip
															label={notificationsLang.matrixLockedTooltip(
																scopeOriginLabel[effective.lock_origin ?? ""] ?? effective.lock_origin ?? ""
															)}
															hasArrow
														>
															<span data-testid={`matrix-lock-icon-${type.key}-${channel}`}>
																<FiLock className="text-gray-500" size={14} />
															</span>
														</ChakraTooltip>
													)}
													<SelectWithLabel
														noLabel
														name={`matrix-delivery-${type.key}-${channel}`}
														testId={`matrix-delivery-select-${type.key}-${channel}`}
														options={DELIVERY_OPTIONS}
														value={DELIVERY_OPTIONS.find(
																(option) => option.value === (isSecurity ? "immediate" : effective.delivery)
															)}
														disabled={isDisabled}
														width="150px"
														className={isOverridden ? "font-semibold" : "italic text-gray-500"}
														onChange={(option) => {
															if (option && !Array.isArray(option)) {
																handleDeliveryChange(type, channel, option.value as NotificationDelivery);
															}
														}}
													/>
													<span
														className="text-[10px] uppercase tracking-wide text-gray-400"
														data-testid={`matrix-source-label-${type.key}-${channel}`}
													>
														{isOverridden
															? notificationsLang.matrixOverriddenLabel
															: notificationsLang.matrixInheritedLabel}
													</span>
													{isOverridden && !isDisabled && (
														<button
															type="button"
															title={notificationsLang.matrixRevertToInherited}
															data-testid={`matrix-revert-${type.key}-${channel}`}
															className="text-gray-400 hover:text-gray-600"
															onClick={() => handleRevert(type, channel)}
														>
															<FiRotateCcw size={14} />
														</button>
													)}
												</div>
											</td>
										);
									})}
								</tr>
							))}
						</React.Fragment>
					))}
				</tbody>
			</table>
		</div>
	);
};
